import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import {
  OPS_SERVICE_STATUSES,
  OPS_SERVICE_TYPES,
  type OpsService,
  type OpsServiceInput,
  type OpsServiceStatus,
  type OpsServiceType,
  toInputDateValue,
} from '../../utils/opsServices';

interface OpsServiceFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  initial?: OpsService | null;
  defaultDate?: string;
  /** Prefill when adding a new service (e.g. tour title + pax from WC line). */
  prefill?: Partial<OpsServiceInput> | null;
  bookingNumber?: string;
  customerName?: string;
  onSave: (input: OpsServiceInput) => Promise<void> | void;
  saving?: boolean;
}

const emptyForm = (
  defaultDate?: string,
  prefill?: Partial<OpsServiceInput> | null
): OpsServiceInput => ({
  serviceDate: prefill?.serviceDate || defaultDate || toInputDateValue(),
  serviceType: prefill?.serviceType || 'tour',
  serviceName: prefill?.serviceName || '',
  fileNo: prefill?.fileNo || '',
  agency: prefill?.agency || '',
  nationality: prefill?.nationality || '',
  pax: prefill?.pax ?? null,
  includedPax: prefill?.includedPax ?? null,
  optionalPax: prefill?.optionalPax ?? null,
  hotel: prefill?.hotel || '',
  pickupTime: prefill?.pickupTime || '',
  serviceTime: prefill?.serviceTime || '',
  flightNumber: prefill?.flightNumber || '',
  flightTime: prefill?.flightTime || '',
  from: prefill?.from || '',
  to: prefill?.to || '',
  guide: prefill?.guide || '',
  coordinator: prefill?.coordinator || '',
  vehicle: prefill?.vehicle || '',
  driver: prefill?.driver || '',
  notes: '',
  status: prefill?.status || 'scheduled',
});

const OpsServiceFormModal: React.FC<OpsServiceFormModalProps> = ({
  isOpen,
  toggle,
  initial,
  defaultDate,
  prefill,
  bookingNumber,
  customerName,
  onSave,
  saving = false,
}) => {
  const [form, setForm] = useState<OpsServiceInput>(emptyForm(defaultDate, prefill));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (initial) {
      setForm({ ...initial });
    } else {
      setForm(emptyForm(defaultDate, prefill));
    }
    setError(null);
  }, [isOpen, initial, defaultDate, prefill]);

  const setField = <K extends keyof OpsServiceInput>(key: K, value: OpsServiceInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const isTour = form.serviceType === 'tour';
  const isArrDep = form.serviceType === 'arrival' || form.serviceType === 'departure';

  const onArrDepChange = (nextType: 'arrival' | 'departure') => {
    setForm((prev) => ({
      ...prev,
      serviceType: nextType,
      serviceName: nextType === 'arrival' ? 'Arrival' : 'Departure',
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isArrDep) {
      if (!form.serviceDate) {
        setError('Date is required.');
        return;
      }
      setError(null);
      await onSave({
        ...form,
        id: initial?.id,
        serviceType: form.serviceType,
        serviceName: form.serviceType === 'arrival' ? 'Arrival' : 'Departure',
        status: form.status || 'scheduled',
        guide: null,
        includedPax: null,
        optionalPax: null,
        pickupTime: null,
        notes: null,
      });
      return;
    }

    const lockedTourName =
      (initial?.serviceType === 'tour' && initial.serviceName) ||
      (prefill?.serviceName && String(prefill.serviceName).trim()) ||
      form.serviceName;

    const serviceName = isTour
      ? String(lockedTourName || '').trim()
      : String(form.serviceName || '').trim();

    if (!form.serviceDate || !serviceName) {
      setError(
        isTour
          ? 'Date is required, and this booking needs a tour name from the booked tour.'
          : 'Date and service name are required.'
      );
      return;
    }
    setError(null);

    const payload: OpsServiceInput = {
      ...form,
      id: initial?.id,
      serviceName,
    };

    if (payload.serviceType === 'tour' || isTour) {
      payload.serviceType = 'tour';
      payload.status = form.status || 'scheduled';
      payload.hotel = null;
      payload.notes = null;
      payload.flightNumber = null;
      payload.flightTime = null;
      payload.from = null;
      payload.to = null;
    }

    await onSave(payload);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={toggle}>
          {initial ? 'Edit service' : 'Add service'}
        </ModalHeader>
        <ModalBody>
          {error ? <div className="alert alert-danger py-2">{error}</div> : null}

          {(bookingNumber || customerName) && (
            <Row className="mb-2">
              <Col md={6}>
                <FormGroup>
                  <Label className="text-muted">Booking Number</Label>
                  <Input type="text" value={bookingNumber || '—'} plaintext readOnly />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="text-muted">Name</Label>
                  <Input type="text" value={customerName || '—'} plaintext readOnly />
                </FormGroup>
              </Col>
            </Row>
          )}

          <Row>
            <Col md={3}>
              <FormGroup>
                <Label>Code</Label>
                <Input
                  type="text"
                  value={form.fileNo || ''}
                  onChange={(e) => setField('fileNo', e.target.value)}
                  placeholder="Code"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Agency</Label>
                <Input
                  type="text"
                  value={form.agency || ''}
                  onChange={(e) => setField('agency', e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Nationality</Label>
                <Input
                  type="text"
                  value={form.nationality || ''}
                  onChange={(e) => setField('nationality', e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Status</Label>
                <Input
                  type="select"
                  value={form.status}
                  onChange={(e) =>
                    setField('status', e.target.value as OpsServiceStatus)
                  }
                >
                  {OPS_SERVICE_STATUSES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          {isArrDep ? (
            <>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={form.serviceDate}
                      onChange={(e) => setField('serviceDate', e.target.value)}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Arrival / Departure *</Label>
                    <Input
                      type="select"
                      value={form.serviceType}
                      onChange={(e) =>
                        onArrDepChange(e.target.value as 'arrival' | 'departure')
                      }
                    >
                      <option value="arrival">Arrival</option>
                      <option value="departure">Departure</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Passenger</Label>
                    <Input
                      type="number"
                      min={0}
                      value={form.pax ?? ''}
                      onChange={(e) =>
                        setField('pax', e.target.value === '' ? null : Number(e.target.value))
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={8}>
                  <FormGroup>
                    <Label>Hotel name</Label>
                    <Input
                      type="text"
                      value={form.hotel || ''}
                      onChange={(e) => setField('hotel', e.target.value)}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={3}>
                  <FormGroup>
                    <Label>Flight No</Label>
                    <Input
                      type="text"
                      value={form.flightNumber || ''}
                      onChange={(e) => setField('flightNumber', e.target.value)}
                      placeholder="EK722"
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>From</Label>
                    <Input
                      type="text"
                      value={form.from || ''}
                      onChange={(e) => setField('from', e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>To</Label>
                    <Input
                      type="text"
                      value={form.to || ''}
                      onChange={(e) => setField('to', e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Flight time</Label>
                    <Input
                      type="time"
                      value={form.flightTime || ''}
                      onChange={(e) => setField('flightTime', e.target.value)}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={3}>
                  <FormGroup>
                    <Label>Coordinator</Label>
                    <Input
                      type="text"
                      value={form.coordinator || ''}
                      onChange={(e) => setField('coordinator', e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Car</Label>
                    <Input
                      type="text"
                      value={form.vehicle || ''}
                      onChange={(e) => setField('vehicle', e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Driver name</Label>
                    <Input
                      type="text"
                      value={form.driver || ''}
                      onChange={(e) => setField('driver', e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className="mb-0">
                    <Label>Time</Label>
                    <Input
                      type="time"
                      value={form.serviceTime || ''}
                      onChange={(e) => setField('serviceTime', e.target.value)}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </>
          ) : (
            <>
              <Row>
                <Col md={isTour ? 12 : 6}>
                  <FormGroup>
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={form.serviceDate}
                      onChange={(e) => setField('serviceDate', e.target.value)}
                      required
                    />
                  </FormGroup>
                </Col>
                {!isTour ? (
                  <Col md={6}>
                    <FormGroup>
                      <Label>Type *</Label>
                      <Input
                        type="select"
                        value={form.serviceType}
                        onChange={(e) =>
                          setField('serviceType', e.target.value as OpsServiceType)
                        }
                      >
                        {OPS_SERVICE_TYPES.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                ) : null}
              </Row>

              <FormGroup>
                <Label>{isTour ? 'Tour name' : 'Service name *'}</Label>
                {isTour ? (
                  <Input
                    type="text"
                    value={form.serviceName || '—'}
                    plaintext
                    readOnly
                    className="fw-medium"
                  />
                ) : (
                  <Input
                    type="text"
                    value={form.serviceName || ''}
                    onChange={(e) => setField('serviceName', e.target.value)}
                    placeholder="e.g. Hotel transfer"
                    required
                  />
                )}
              </FormGroup>

              {isTour ? (
                <>
                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Passenger</Label>
                        <Input
                          type="number"
                          min={0}
                          value={form.pax ?? ''}
                          onChange={(e) =>
                            setField(
                              'pax',
                              e.target.value === '' ? null : Number(e.target.value)
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>INC</Label>
                        <Input
                          type="number"
                          min={0}
                          value={form.includedPax ?? ''}
                          onChange={(e) =>
                            setField(
                              'includedPax',
                              e.target.value === '' ? null : Number(e.target.value)
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>OPT</Label>
                        <Input
                          type="number"
                          min={0}
                          value={form.optionalPax ?? ''}
                          onChange={(e) =>
                            setField(
                              'optionalPax',
                              e.target.value === '' ? null : Number(e.target.value)
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Pickup Time</Label>
                        <Input
                          type="time"
                          value={form.pickupTime || ''}
                          onChange={(e) => setField('pickupTime', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Time</Label>
                        <Input
                          type="time"
                          value={form.serviceTime || ''}
                          onChange={(e) => setField('serviceTime', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Guide</Label>
                        <Input
                          type="text"
                          value={form.guide || ''}
                          onChange={(e) => setField('guide', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Coordinator</Label>
                        <Input
                          type="text"
                          value={form.coordinator || ''}
                          onChange={(e) => setField('coordinator', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Car</Label>
                        <Input
                          type="text"
                          value={form.vehicle || ''}
                          onChange={(e) => setField('vehicle', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup className="mb-0">
                        <Label>Driver Name</Label>
                        <Input
                          type="text"
                          value={form.driver || ''}
                          onChange={(e) => setField('driver', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Pax</Label>
                        <Input
                          type="number"
                          min={0}
                          value={form.pax ?? ''}
                          onChange={(e) =>
                            setField(
                              'pax',
                              e.target.value === '' ? null : Number(e.target.value)
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Hotel / pickup</Label>
                        <Input
                          type="text"
                          value={form.hotel || ''}
                          onChange={(e) => setField('hotel', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Pickup time</Label>
                        <Input
                          type="time"
                          value={form.pickupTime || ''}
                          onChange={(e) => setField('pickupTime', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Guide</Label>
                        <Input
                          type="text"
                          value={form.guide || ''}
                          onChange={(e) => setField('guide', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Coordinator</Label>
                        <Input
                          type="text"
                          value={form.coordinator || ''}
                          onChange={(e) => setField('coordinator', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Car</Label>
                        <Input
                          type="text"
                          value={form.vehicle || ''}
                          onChange={(e) => setField('vehicle', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Driver Name</Label>
                        <Input
                          type="text"
                          value={form.driver || ''}
                          onChange={(e) => setField('driver', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup className="mb-0">
                    <Label>Notes</Label>
                    <Input
                      type="textarea"
                      rows={2}
                      value={form.notes || ''}
                      onChange={(e) => setField('notes', e.target.value)}
                    />
                  </FormGroup>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="secondary" outline onClick={toggle} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save service'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default OpsServiceFormModal;
